import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectApiService, Project } from '../../recruiter/services/projectApi';

// Dropdown option constants (same as CompanyJobDetails edit mode)
const EXPERIENCE_LEVEL_OPTIONS = [
    'Entry-level',
    'Junior',
    'Mid-level',
    'Senior',
    'Lead',
    'Principal',
    'Executive',
];

const EMPLOYMENT_TYPE_OPTIONS = [
    'Full-time',
    'Part-time',
    'Contract',
    'Freelance',
    'Internship',
    'Temporary',
];

const WORK_MODE_OPTIONS = [
    'Remote',
    'On-site',
    'Hybrid',
];

const EDUCATION_OPTIONS = [
    'No Requirement',
    'High School Diploma',
    "Associate's Degree",
    "Bachelor's Degree",
    "Master's Degree",
    'Doctoral Degree / PhD',
    'Professional Degree (MD, JD, etc.)',
];

const COUNTRIES = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
    'France', 'India', 'China', 'Japan', 'Singapore', 'Brazil', 'Mexico',
    'Netherlands', 'Sweden', 'Switzerland', 'South Korea', 'Israel',
    'United Arab Emirates', 'Ireland', 'New Zealand', 'Other',
];

/** Tag input component for skills/keywords/benefits (same as CompanyJobDetails) */
const TagInput: React.FC<{
    tags: string[];
    onAdd: (tag: string) => void;
    onRemove: (index: number) => void;
    placeholder: string;
    colorClass: string;
}> = ({ tags, onAdd, onRemove, placeholder, colorClass }) => {
    const [input, setInput] = useState('');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
            e.preventDefault();
            onAdd(input.trim());
            setInput('');
        }
        if (e.key === 'Backspace' && !input && tags.length > 0) {
            onRemove(tags.length - 1);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus-within:ring-2 focus-within:ring-[#015FA6] bg-white min-h-[48px]">
            {tags.map((tag, i) => (
                <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium ${colorClass}`}>
                    {tag}
                    <button type="button" onClick={() => onRemove(i)} className="hover:opacity-70">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            ))}
            <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tags.length === 0 ? placeholder : 'Add more...'}
                className="flex-1 min-w-[120px] outline-none text-base text-[#0B111E] bg-transparent"
                style={{ fontFamily: 'Manrope, sans-serif' }}
            />
        </div>
    );
};

interface JobFormData {
    job_title: string;
    job_description: string;
    location_country: string;
    location_city: string;
    employment_type: string;
    experience_level: string;
    work_mode: string;
    salary_range: string;
    education: string;
    min_years_experience: number;
    required_skills: string[];
    preferred_skills: string[];
    keywords: string[];
    required_degrees: string[];
    responsibilities: string[];
    benefits: string[];
    company_name: string;
}

const JobPostingForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Get projectId from navigation state (if coming from a project)
    const initialProjectId = (location.state as any)?.projectId;

    // Project selection state
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProjectId || '');
    const [isLoadingProjects, setIsLoadingProjects] = useState(true);
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [isCreatingProject, setIsCreatingProject] = useState(false);

    // Fetch projects on mount
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setIsLoadingProjects(true);
                const response = await projectApiService.listProjects(1, 100);
                setProjects(response.data || []);

                if (initialProjectId) {
                    setSelectedProjectId(initialProjectId);
                }
            } catch (error) {
                console.error('Failed to fetch projects:', error);
            } finally {
                setIsLoadingProjects(false);
            }
        };

        fetchProjects();
    }, [initialProjectId]);

    // Handle creating a new project
    const handleCreateProject = async () => {
        if (!newProjectName.trim()) return;

        try {
            setIsCreatingProject(true);
            const newProject = await projectApiService.createProject(newProjectName.trim());

            setProjects(prev => [...prev, newProject]);
            setSelectedProjectId(newProject._id || newProject.projectId);

            setShowCreateProjectModal(false);
            setNewProjectName('');
        } catch (error) {
            console.error('Failed to create project:', error);
            alert('Failed to create project. Please try again.');
        } finally {
            setIsCreatingProject(false);
        }
    };

    // Get company name from localStorage
    const getCompanyName = () => {
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                return userData.first_name || userData.company_name || 'Company';
            } catch (error) {
                console.error('Failed to parse user data:', error);
            }
        }
        return 'Company';
    };

    const [formData, setFormData] = useState<JobFormData>({
        job_title: '',
        job_description: '',
        location_country: '',
        location_city: '',
        employment_type: '',
        experience_level: '',
        work_mode: '',
        salary_range: '',
        education: '',
        min_years_experience: 0,
        required_skills: [],
        preferred_skills: [],
        keywords: [],
        required_degrees: [],
        responsibilities: [],
        benefits: [],
        company_name: getCompanyName(),
    });

    const updateField = (field: keyof JobFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addTag = (field: 'required_skills' | 'preferred_skills' | 'keywords' | 'required_degrees' | 'responsibilities' | 'benefits', tag: string) => {
        const current = formData[field];
        if (!current.includes(tag)) {
            updateField(field, [...current, tag]);
        }
    };

    const removeTag = (field: 'required_skills' | 'preferred_skills' | 'keywords' | 'required_degrees' | 'responsibilities' | 'benefits', index: number) => {
        const current = formData[field];
        updateField(field, current.filter((_: string, i: number) => i !== index));
    };

    const handleContinueWithDavid = () => {
        setIsSubmitting(true);

        const projectIdToUse = selectedProjectId || undefined;
        console.log('🔍 JobPostingForm - Passing projectId to Talk to David:', projectIdToUse);

        navigate('/companies/talk-to-david', {
            state: {
                formData: formData,
                fromForm: true,
                projectId: projectIdToUse
            }
        });

        setIsSubmitting(false);
    };

    return (
        <div className="space-y-4">
            {/* Project / Folder Section */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Project / Folder
                </h2>
                <p className="text-sm text-[#757575] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Organize your job postings by assigning them to a project folder.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <select
                            value={selectedProjectId}
                            onChange={(e) => setSelectedProjectId(e.target.value)}
                            disabled={isLoadingProjects}
                            className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">
                                {isLoadingProjects ? 'Loading projects...' : 'Default Project (No folder selected)'}
                            </option>
                            {projects.map((project) => (
                                <option key={project._id || project.projectId} value={project._id || project.projectId}>
                                    {project.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowCreateProjectModal(true)}
                        className="px-5 py-3 border border-[#015FA6] text-[#015FA6] font-medium rounded-lg hover:bg-[#F0F7FF] transition-colors flex items-center justify-center gap-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        New Folder
                    </button>
                </div>
                {selectedProjectId && (
                    <div className="mt-3">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0F7FF] text-[#015FA6] rounded-lg text-sm font-medium">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V9C21 7.89543 20.1046 7 19 7H13L11 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {projects.find(p => (p._id || p.projectId) === selectedProjectId)?.name || 'Selected Project'}
                            <button
                                type="button"
                                onClick={() => setSelectedProjectId('')}
                                className="hover:opacity-70"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </button>
                        </span>
                    </div>
                )}
            </div>

            {/* Job Title */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Job Title
                </h2>
                <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => updateField('job_title', e.target.value)}
                    placeholder="e.g. Senior Product Designer"
                    className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base text-[#0B111E]"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                />
            </div>

            {/* Info Bar - Location, Employment Type, Experience Level, Work Mode, Salary */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Job Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Country</label>
                        <select
                            value={formData.location_country}
                            onChange={(e) => updateField('location_country', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">Select country...</option>
                            {COUNTRIES.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>City</label>
                        <input
                            value={formData.location_city}
                            onChange={(e) => updateField('location_city', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            placeholder="e.g. San Francisco"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Employment Type</label>
                        <select
                            value={formData.employment_type}
                            onChange={(e) => updateField('employment_type', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">Select...</option>
                            {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Experience Level</label>
                        <select
                            value={formData.experience_level}
                            onChange={(e) => updateField('experience_level', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">Select...</option>
                            {EXPERIENCE_LEVEL_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Work Mode</label>
                        <select
                            value={formData.work_mode}
                            onChange={(e) => updateField('work_mode', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">Select...</option>
                            {WORK_MODE_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Salary Range</label>
                        <input
                            value={formData.salary_range}
                            onChange={(e) => updateField('salary_range', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            placeholder="e.g. $80,000 - $120,000"
                        />
                    </div>
                </div>
            </div>

            {/* Job Description */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Job Description
                </h2>
                <textarea
                    value={formData.job_description}
                    onChange={(e) => updateField('job_description', e.target.value)}
                    rows={8}
                    className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base text-[#0B111E] resize-y"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    placeholder="Describe the role, responsibilities, and what makes this position unique..."
                />
                <p className="text-xs text-[#A5A5A5] mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formData.job_description.length} / 5000 characters
                </p>
            </div>

            {/* Responsibilities */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Responsibilities
                </h2>
                <TagInput
                    tags={formData.responsibilities}
                    onAdd={(tag) => addTag('responsibilities', tag)}
                    onRemove={(i) => removeTag('responsibilities', i)}
                    placeholder="Type a responsibility and press Enter..."
                    colorClass="bg-[#F0F7FF] text-[#015FA6]"
                />
            </div>

            {/* Skills & Keywords */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Skills & Keywords
                </h2>

                {/* Required Skills */}
                <div className="mb-5">
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Required Skills</h3>
                    <TagInput
                        tags={formData.required_skills}
                        onAdd={(tag) => addTag('required_skills', tag)}
                        onRemove={(i) => removeTag('required_skills', i)}
                        placeholder="Type a skill and press Enter..."
                        colorClass="bg-[#F0F7FF] text-[#015FA6]"
                    />
                </div>

                {/* Preferred Skills */}
                <div className="mb-5">
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Skills</h3>
                    <TagInput
                        tags={formData.preferred_skills}
                        onAdd={(tag) => addTag('preferred_skills', tag)}
                        onRemove={(i) => removeTag('preferred_skills', i)}
                        placeholder="Type a skill and press Enter..."
                        colorClass="bg-[#F0FFF4] text-[#16A34A]"
                    />
                </div>

                {/* Keywords */}
                <div>
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Keywords</h3>
                    <TagInput
                        tags={formData.keywords}
                        onAdd={(tag) => addTag('keywords', tag)}
                        onRemove={(i) => removeTag('keywords', i)}
                        placeholder="Type a keyword and press Enter..."
                        colorClass="bg-[#EDE9FE] text-[#6B21A8]"
                    />
                </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Requirements
                </h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Minimum Years of Experience</label>
                        <input
                            type="number"
                            value={formData.min_years_experience || 0}
                            onChange={(e) => updateField('min_years_experience', parseInt(e.target.value) || 0)}
                            className="w-full max-w-[200px] px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            min={0}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Education</label>
                        <select
                            value={formData.education}
                            onChange={(e) => updateField('education', e.target.value)}
                            className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base bg-white"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            <option value="">Select education level...</option>
                            {EDUCATION_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Required Degrees</label>
                        <TagInput
                            tags={formData.required_degrees}
                            onAdd={(tag) => addTag('required_degrees', tag)}
                            onRemove={(i) => removeTag('required_degrees', i)}
                            placeholder="Type a degree and press Enter..."
                            colorClass="bg-[#FFF8F0] text-[#D97706]"
                        />
                    </div>
                </div>
            </div>

            {/* Work Details & Benefits */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Benefits & Perks
                </h2>
                <TagInput
                    tags={formData.benefits}
                    onAdd={(tag) => addTag('benefits', tag)}
                    onRemove={(i) => removeTag('benefits', i)}
                    placeholder="Type a benefit and press Enter..."
                    colorClass="bg-[#F0FFF4] text-[#16A34A]"
                />
            </div>

            {/* Continue with David Button */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleContinueWithDavid}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Continuing...
                            </>
                        ) : (
                            <>
                                Continue with David
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M6 12L10 8L6 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </>
                        )}
                    </button>
                    <p className="text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        David will review and refine your job posting
                    </p>
                </div>
            </div>

            {/* Create Project Modal */}
            {showCreateProjectModal && (
                <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
                        <h3 className="text-xl font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Create New Folder
                        </h3>
                        <p className="text-sm text-[#757575] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Create folders to group similar roles or teams
                        </p>
                        <input
                            type="text"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
                            placeholder="Enter folder name..."
                            autoFocus
                            className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base mb-6"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        />
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateProjectModal(false);
                                    setNewProjectName('');
                                }}
                                className="flex-1 px-6 py-3 border border-[#A5A5A5] text-[#757575] font-medium rounded-lg hover:bg-[#F4F4F4] transition-colors"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateProject}
                                disabled={!newProjectName.trim() || isCreatingProject}
                                className="flex-1 px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                {isCreatingProject ? 'Creating...' : 'Create Folder'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobPostingForm;
